import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, Check, Loader2, Sparkles, ShieldCheck, Infinity as InfinityIcon, Ticket } from "lucide-react";

const PERKS = [
  "Unlimited chats with all 4 AI coaches",
  "Voice-enabled Speaking Coach",
  "80 real TCF mock exams (40 Reading + 40 Listening) with transcripts",
  "Priority updates on the latest exam topics",
];

export default function Premium() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const redeem = async () => {
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/promo/redeem", { code: redeemCode });
      await refreshUser(); load();
      toast.success(data.message || "Premium activated! ✦");
      setRedeemCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid promo code."); }
    finally { setRedeeming(false); }
  };

  const load = useCallback(() => {
    api.get("/subscription/status").then((r) => { setStatus(r.data); setPackages(r.data.packages || []); }).catch((e) => console.error("Failed to load subscription status", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe return
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refreshUser(); load();
          toast.success("Payment successful — welcome to Premium! ✦");
          setPolling(false);
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") { toast.error("Payment session expired."); setPolling(false); return; }
      } catch { /* keep trying */ }
      if (attempts >= 6) { toast.info("Still processing — refresh in a moment."); setPolling(false); return; }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async (packageId) => {
    setLoadingId(packageId);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: packageId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout.");
      setLoadingId(null);
    }
  };

  const isPremium = user?.is_premium;

  return (
    <div data-testid="premium-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold mb-4"><Crown className="h-3.5 w-3.5" /> All In One French Premium</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold">Unlimited coaching. Zero limits.</h1>
        <p className="text-muted-foreground mt-3">Your free day gives you a taste. Premium keeps every coach available whenever you need them.</p>
      </div>

      {polling && (
        <div className="mt-6 max-w-md mx-auto rounded-xl bg-accent text-accent-foreground px-4 py-3 text-sm flex items-center gap-2" data-testid="payment-polling">
          <Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment…
        </div>
      )}

      {isPremium ? (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10 max-w-md mx-auto rounded-3xl emerald-surface grain relative overflow-hidden p-8 text-center" data-testid="premium-active-card">
          <Crown className="h-10 w-10 mx-auto text-white" />
          <h2 className="font-display text-2xl font-semibold text-white mt-3">You're Premium ✦</h2>
          <p className="text-white/85 mt-2 text-sm">Active until {user?.premium_until ? new Date(user.premium_until).toLocaleDateString("en-CA", { day: "numeric", month: "long", year: "numeric" }) : "—"}</p>
          <button onClick={() => navigate("/coaches")} className="mt-5 px-6 py-3 rounded-full bg-white text-primary font-semibold">Open a coach</button>
        </motion.div>
      ) : (
        <>
          <div className="grid gap-5 mt-10 max-w-md mx-auto">
            {packages.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                className="relative rounded-3xl border border-primary bg-accent shadow-lg p-7" data-testid={`plan-${p.id}`}>
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full emerald-surface text-white text-xs font-bold shadow" data-testid="early-launch-badge">
                  <Sparkles className="h-3.5 w-3.5" /> Early-launch price
                </span>
                <h3 className="font-semibold text-lg mt-2">{p.name}</h3>
                <div className="mt-2 flex items-end gap-2">
                  <span className="font-display text-4xl font-semibold" data-testid="plan-price">${p.amount.toFixed(2)}</span>
                  <span className="text-muted-foreground mb-1">{(p.currency || "cad").toUpperCase()} / month</span>
                </div>
                {p.original_amount && p.original_amount > p.amount && (
                  <div className="mt-1 flex items-center gap-2 text-sm" data-testid="plan-original-price">
                    <span className="text-muted-foreground line-through">${p.original_amount.toFixed(2)} {(p.currency || "cad").toUpperCase()}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold text-xs">
                      Save {Math.round((1 - p.amount / p.original_amount) * 100)}%
                    </span>
                  </div>
                )}
                <ul className="mt-5 space-y-2">
                  {PERKS.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {perk}</li>
                  ))}
                </ul>
                <button onClick={() => checkout(p.id)} disabled={loadingId === p.id} data-testid={`checkout-${p.id}`}
                  className="mt-6 w-full h-12 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                  {loadingId === p.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Crown className="h-4 w-4" /> Subscribe — ${p.amount.toFixed(2)} {(p.currency || "cad").toUpperCase()}/mo</>}
                </button>
                <p className="text-xs text-muted-foreground text-center mt-3">One-time payment for 30 days. Renew anytime.</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Secure Stripe checkout</span>
            <span className="inline-flex items-center gap-1.5"><InfinityIcon className="h-4 w-4 text-primary" /> Cancel anytime</span>
          </div>
        </>
      )}

      <div className="mt-10 max-w-md mx-auto rounded-2xl bg-card border border-border p-6 text-center" data-testid="promo-redeem-card">
        <Ticket className="h-6 w-6 text-primary mx-auto" />
        <h3 className="font-semibold mt-2">Have a promo code?</h3>
        <p className="text-sm text-muted-foreground mt-1">From a partner institution or teacher? Redeem it to unlock Premium.</p>
        <div className="mt-4 flex gap-2">
          <input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="ENTER CODE" data-testid="redeem-code-input"
            className="flex-1 h-11 px-4 rounded-xl bg-background border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" />
          <button onClick={redeem} disabled={redeeming || !redeemCode.trim()} data-testid="redeem-btn"
            className="px-5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
            {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
          </button>
        </div>
      </div>
    </div>
  );
}
